/* Dorm Dash A2: box card row scroller.
 *
 * The row of box cards scrolls horizontally when the cards do not fit. On a
 * phone that is obvious because you swipe. On a desktop mouse there is no
 * such cue, and the scrollbar is deliberately thin, so this adds prev/next
 * arrows and toggles the edge fades.
 *
 * Everything here is progressive enhancement. With JS off the row still
 * scrolls natively; you just lose the arrows and the fades.
 */
(function () {
  'use strict';

  var SELECTOR = '.dda2-cards';

  function makeButton(direction, label) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dda2-cards-btn';
    btn.setAttribute('aria-label', label);
    btn.dataset.dir = direction;
    // Inline SVG so there is no extra request and it inherits currentColor.
    btn.innerHTML =
      direction === 'prev'
        ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15.4 7.4 14 6l-6 6 6 6 1.4-1.4-4.6-4.6z"/></svg>'
        : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8.6 7.4 10 6l6 6-6 6-1.4-1.4 4.6-4.6z"/></svg>';
    return btn;
  }

  function Scroller(row) {
    this.row = row;

    // Two wrappers, not one, and the reason matters.
    //   .dda2-cards-outer  holds the row AND the arrows
    //   .dda2-cards-wrap   holds ONLY the row, so the edge fades can be its
    //                      pseudo-elements and line up with the row exactly
    // A single wrapper would stretch the fades down over the arrows.
    //
    // The fades cannot live on .dda2-cards itself: that is a
    // grid-auto-flow: column grid, so ::before/::after would each become a
    // phantom extra column.
    var outer = document.createElement('div');
    outer.className = 'dda2-cards-outer';
    row.parentNode.insertBefore(outer, row);

    var wrap = document.createElement('div');
    wrap.className = 'dda2-cards-wrap';
    outer.appendChild(wrap);
    wrap.appendChild(row);
    this.wrap = wrap;
    this.outer = outer;

    var nav = document.createElement('div');
    nav.className = 'dda2-cards-nav';
    this.prev = makeButton('prev', 'Show previous boxes');
    this.next = makeButton('next', 'Show more boxes');
    nav.appendChild(this.prev);
    nav.appendChild(this.next);
    outer.appendChild(nav);

    this.onPrev = this.scrollBy.bind(this, -1);
    this.onNext = this.scrollBy.bind(this, 1);
    this.prev.addEventListener('click', this.onPrev);
    this.next.addEventListener('click', this.onNext);

    this.update = this.update.bind(this);
    row.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.update);

    // Images load late and change the scroll width, so recheck when they land.
    if (typeof ResizeObserver === 'function') {
      this.ro = new ResizeObserver(this.update);
      this.ro.observe(row);
    }

    this.update();
  }

  // One card plus one gap, so a press advances by exactly one card.
  Scroller.prototype.step = function () {
    var card = this.row.querySelector('.dda2-card');
    if (!card) return this.row.clientWidth;
    var gap = parseFloat(getComputedStyle(this.row).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  };

  // Smooth by choice, instant for anyone who asked for reduced motion. Checked
  // at call time rather than cached, so a mid-session settings change is honoured.
  Scroller.prototype.behavior = function () {
    var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    return mq && mq.matches ? 'auto' : 'smooth';
  };

  Scroller.prototype.scrollBy = function (sign) {
    this.row.scrollBy({ left: sign * this.step(), behavior: this.behavior() });
    // Do not rely solely on the scroll event to refresh the arrows. It is async,
    // and a smooth scroll finishes well after the click. Refresh on the next
    // frame and again once the animation has had time to land, so the disabled
    // states cannot get stuck if a scroll event is missed or coalesced.
    var self = this;
    if (window.requestAnimationFrame) window.requestAnimationFrame(self.update);
    setTimeout(self.update, 450);
  };

  Scroller.prototype.update = function () {
    var row = this.row;
    // 2px of tolerance: sub-pixel widths otherwise leave the end arrow enabled
    // on a row that is already fully scrolled.
    var max = row.scrollWidth - row.clientWidth;
    var scrollable = max > 2;
    var atStart = row.scrollLeft <= 2;
    var atEnd = row.scrollLeft >= max - 2;

    this.outer.classList.toggle('is-scrollable', scrollable);
    this.wrap.classList.toggle('is-scrollable-start', scrollable && !atStart);
    this.wrap.classList.toggle('is-scrollable-end', scrollable && !atEnd);

    this.prev.disabled = !scrollable || atStart;
    this.next.disabled = !scrollable || atEnd;
  };

  function boot() {
    var rows = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < rows.length; i++) {
      if (!rows[i].dataset.dda2Scroller) {
        rows[i].dataset.dda2Scroller = '1';
        new Scroller(rows[i]);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', boot);
})();
